'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { apiService, UserInfo } from '@/apiHuiTun/api';

interface AuthContextType {
  user: UserInfo | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, tel: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuthToken: () => Promise<void>;
  refreshUserInfo: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  const isAuthenticated = !!user && !!accessToken;

  // Helper functions for cookie management
  const setCookie = (name: string, value: string, days: number = 7) => {
    if (typeof window === 'undefined') return;
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  };

  const deleteCookie = (name: string) => {
    if (typeof window === 'undefined') return;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  };

  // Load tokens from localStorage on mount
  useEffect(() => {
    const loadStoredTokens = () => {
      try {
        if (typeof window === 'undefined') return;
        
        const storedAccessToken = localStorage.getItem('access_token');
        const storedRefreshToken = localStorage.getItem('refresh_token');
        const storedUser = localStorage.getItem('user');

        if (storedAccessToken && storedRefreshToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setAccessToken(storedAccessToken);
          setRefreshToken(storedRefreshToken);
          setUser(parsedUser);
          // Also set cookies for middleware
          setCookie('access_token', storedAccessToken, 7);
          setCookie('refresh_token', storedRefreshToken, 30);

          // Silent sync with the database to get fresh user info (balance, sub, etc.)
          apiService.getUserInfo(storedAccessToken).then((response) => {
            if (response.success && response.data) {
              setUser(response.data.resource_owner);
              localStorage.setItem('user', JSON.stringify(response.data.resource_owner));
            }
          }).catch(err => console.error("Silent user info sync failed:", err));
        }
      } catch (error) {
        console.error('Error loading stored tokens:', error);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          deleteCookie('access_token');
          deleteCookie('refresh_token');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredTokens();
  }, []);

  // Fetch user info when access token is available
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (accessToken && (!user || user.balance === undefined)) {
        try {
          const response = await apiService.getUserInfo(accessToken);
          if (response.success && response.data) {
            setUser(response.data.resource_owner);
            if (typeof window !== 'undefined') {
              localStorage.setItem('user', JSON.stringify(response.data.resource_owner));
            }
          }
        } catch (error) {
          console.error('Error fetching user info:', error);
          if (refreshToken) {
            await refreshAuthToken();
          } else {
            logout();
          }
        }
      }
    };

    fetchUserInfo();
  }, [accessToken, user, refreshToken]);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await apiService.signIn({ email, password });
      
      if (response.success && response.data) {
        const { access_token, refresh_token } = response.data;
        
        setAccessToken(access_token);
        setRefreshToken(refresh_token);
        
        if (typeof window !== 'undefined') {
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          // Also set cookies for middleware
          setCookie('access_token', access_token, 7);
          setCookie('refresh_token', refresh_token, 30);
        }
        
        // Fetch full user info
        const userInfoResponse = await apiService.getUserInfo(access_token);
        if (userInfoResponse.success && userInfoResponse.data) {
          setUser(userInfoResponse.data.resource_owner);
          if (typeof window !== 'undefined') {
            localStorage.setItem('user', JSON.stringify(userInfoResponse.data.resource_owner));
          }
        }
        
        router.push('/');
      } else {
        // Get error message from errors array first, then from message field
        // Handle both cases: errors as array or errors.errors as array (nested structure)
        let errorMessage = 'Đăng nhập thất bại';
        if (response.errors) {
          if (Array.isArray(response.errors)) {
            errorMessage = response.errors[0];
          } else if (typeof response.errors === 'object' && response.errors !== null && 'errors' in response.errors) {
            const nestedErrors = (response.errors as { errors?: string[] }).errors;
            if (Array.isArray(nestedErrors) && nestedErrors.length > 0) {
              errorMessage = nestedErrors[0];
            }
          } else if (typeof response.errors === 'string') {
            errorMessage = response.errors;
          }
        }
        if (!errorMessage || errorMessage === 'Đăng nhập thất bại') {
          errorMessage = response.message || 'Đăng nhập thất bại';
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string, tel: string) => {
    try {
      setIsLoading(true);
      const response = await apiService.signUp({
        email,
        password,
        password_confirmation: password,
        name,
        tel,
      });
      
      if (response.success && response.data) {
        const { access_token, refresh_token } = response.data;
        
        setAccessToken(access_token);
        setRefreshToken(refresh_token);
        
        if (typeof window !== 'undefined') {
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          // Also set cookies for middleware
          setCookie('access_token', access_token, 7);
          setCookie('refresh_token', refresh_token, 30);
        }
        
        // Fetch full user info
        const userInfoResponse = await apiService.getUserInfo(access_token);
        if (userInfoResponse.success && userInfoResponse.data) {
          setUser(userInfoResponse.data.resource_owner);
          if (typeof window !== 'undefined') {
            localStorage.setItem('user', JSON.stringify(userInfoResponse.data.resource_owner));
          }
        }
        
        router.push('/');
      } else {
        // Get error message from errors array first, then from message field
        // Handle both cases: errors as array or errors.errors as array (nested structure)
        let errorMessage = 'Đăng ký thất bại';
        if (response.errors) {
          if (Array.isArray(response.errors)) {
            errorMessage = response.errors[0];
          } else if (typeof response.errors === 'object' && response.errors !== null && 'errors' in response.errors) {
            const nestedErrors = (response.errors as { errors?: string[] }).errors;
            if (Array.isArray(nestedErrors) && nestedErrors.length > 0) {
              errorMessage = nestedErrors[0];
            }
          } else if (typeof response.errors === 'string') {
            errorMessage = response.errors;
          }
        }
        if (!errorMessage || errorMessage === 'Đăng ký thất bại') {
          errorMessage = response.message || 'Đăng ký thất bại';
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      if (refreshToken) {
        await apiService.signOut(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);
      if (typeof window !== 'undefined') {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        // Also delete cookies
        deleteCookie('access_token');
        deleteCookie('refresh_token');
      }
      router.push('/auth/user/login');
    }
  }, [refreshToken, router]);

  const refreshAuthToken = useCallback(async () => {
    if (!refreshToken) {
      logout();
      return;
    }

    try {
      const response = await apiService.refreshToken(refreshToken);
      
      if (response.success && response.data) {
        const { access_token, refresh_token } = response.data;
        
        setAccessToken(access_token);
        setRefreshToken(refresh_token);
        
        if (typeof window !== 'undefined') {
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          // Also update cookies
          setCookie('access_token', access_token, 7);
          setCookie('refresh_token', refresh_token, 30);
        }
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
    }
  }, [refreshToken, logout]);

  const refreshUserInfo = useCallback(async () => {
    if (!accessToken) return;
    try {
      const response = await apiService.getUserInfo(accessToken);
      if (response.success && response.data) {
        setUser(response.data.resource_owner);
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(response.data.resource_owner));
        }
      }
    } catch (error) {
      console.error('Error refreshing user info:', error);
    }
  }, [accessToken]);

  // Re-run fetchUserInfo when logout or refreshAuthToken changes
  useEffect(() => {
    if (accessToken && (!user || user.balance === undefined)) {
      refreshUserInfo();
    }
  }, [accessToken, user, refreshUserInfo]);

  const value: AuthContextType = {
    user,
    accessToken,
    refreshToken,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshAuthToken,
    refreshUserInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

